import type { ConfidenceLevel, PlanV3 } from "./types";

interface RenameGroupInPlanArgs {
  plan: PlanV3;
  oldGroupLabel: string;
  newGroupLabel: string;
  nameStatus: ConfidenceLevel;
  elementTypeId: number;
}

export const renameGroupInPlan = ({
  plan,
  oldGroupLabel,
  newGroupLabel,
  nameStatus,
  elementTypeId,
}: RenameGroupInPlanArgs): PlanV3 => {
  if (oldGroupLabel === newGroupLabel) return plan;

  const sourceElements = plan.groupedElements.byGroupId[oldGroupLabel];
  if (!sourceElements) return plan;

  const isMerge = newGroupLabel in plan.groupedElements.byGroupId;
  const targetElements = isMerge
    ? (plan.groupedElements.byGroupId[newGroupLabel] ?? [])
    : [];
  const targetColor = targetElements[0]?.rgb;

  const movedElements = sourceElements.map((element) => ({
    ...element,
    group_id: newGroupLabel,
    rgb: targetColor ?? element.rgb,
  }));

  const { [oldGroupLabel]: _sourceElements, ...restByGroupId } =
    plan.groupedElements.byGroupId;

  const byGroupId = {
    ...restByGroupId,
    [newGroupLabel]: isMerge ? [...targetElements, ...movedElements] : movedElements,
  };

  const groupIds = isMerge
    ? plan.groupedElements.groupIds.filter((id) => id !== oldGroupLabel)
    : plan.groupedElements.groupIds.map((id) =>
        id === oldGroupLabel ? newGroupLabel : id,
      );

  const { [oldGroupLabel]: oldMeta, ...restGroupMeta } = plan.groupMeta;
  // A merge deletes one of the two types, so trust the server's id over the local one.
  const survivingMeta = isMerge
    ? (plan.groupMeta[newGroupLabel] ?? oldMeta)
    : oldMeta;

  const groupMeta = survivingMeta
    ? {
        ...restGroupMeta,
        [newGroupLabel]: {
          ...survivingMeta,
          element_type_id: elementTypeId,
          nameStatus,
        },
      }
    : restGroupMeta;

  return {
    ...plan,
    groupedElements: {
      ...plan.groupedElements,
      groupIds,
      byGroupId,
    },
    groupMeta,
  };
};
